import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

function VoucherImport({ open, onClose, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      
      if (
        fileType === "text/csv" ||
        fileName.endsWith(".csv") ||
        fileType === "application/json" ||
        fileName.endsWith(".json")
      ) {
        setFile(selectedFile);
        setError("");
        setResults(null);
      } else {
        setError("Please select a CSV or JSON file");
      }
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map(h => h.trim());
    
    return lines.slice(1).map(line => {
      const values = line.split(",").map(v => v.trim());
      const obj = {};
      
      headers.forEach((header, index) => {
        let value = values[index] || "";
        
        if (header === "value" || header === "min_spend") {
          obj[header] = parseFloat(value);
        } else {
          obj[header] = value;
        }
      });
      
      return obj;
    });
  };

  const validateVoucher = (voucher) => {
    const required = ["code", "description", "discount_type", "value", "min_spend", "expiry_date", "status"];
    const missing = required.filter(field => !voucher[field] && voucher[field] !== 0);
    
    if (missing.length > 0) {
      return `Missing fields: ${missing.join(", ")}`;
    }
    
    if (!["fixed", "percent"].includes(voucher.discount_type)) {
      return "discount_type must be 'fixed' or 'percent'";
    }
    
    if (!["active", "inactive"].includes(voucher.status)) {
      return "status must be 'active' or 'inactive'";
    }
    
    if (isNaN(voucher.value) || voucher.value <= 0) {
      return "value must be a positive number";
    }
    
    if (isNaN(voucher.min_spend) || voucher.min_spend < 0) {
      return "min_spend must be a non-negative number";
    }
    
    return null;
  };

  const handleImport = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const text = await file.text();
      let vouchers = [];
      
      if (file.name.toLowerCase().endsWith(".json")) {
        vouchers = JSON.parse(text);
      } else {
        vouchers = parseCSV(text);
      }

      if (!Array.isArray(vouchers)) {
        throw new Error("File must contain an array of vouchers");
      }

      const validVouchers = [];
      const errors = [];

      for (let i = 0; i < vouchers.length; i++) {
        const voucher = vouchers[i];
        const validationError = validateVoucher(voucher);
        
        if (validationError) {
          errors.push(`Row ${i + 1}: ${validationError}`);
        } else {
          validVouchers.push(voucher);
        }
      }

      if (errors.length > 0 && validVouchers.length === 0) {
        setError(`Validation failed:\n${errors.join("\n")}`);
        return;
      }

      let successCount = 0;
      let failedCount = 0;
      const failedVouchers = [];

      for (const voucher of validVouchers) {
        try {
          await addDoc(collection(db, "vouchers"), {
            code: voucher.code.toUpperCase(),
            description: voucher.description,
            discount_type: voucher.discount_type,
            value: voucher.value,
            min_spend: voucher.min_spend,
            expiry_date: voucher.expiry_date,
            status: voucher.status,
          });
          successCount++;
        } catch (err) {
          failedCount++;
          failedVouchers.push(`${voucher.code}: ${err.message}`);
        }
      }

      setResults({
        total: vouchers.length,
        successful: successCount,
        failed: failedCount,
        validationErrors: errors,
        importErrors: failedVouchers,
      });

      if (successCount > 0) {
        onImportComplete?.();
      }

    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    setResults(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Vouchers</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a CSV or JSON file with voucher data. Required fields: code, description, discount_type (fixed/percent), value, min_spend, expiry_date (YYYY-MM-DD), status (active/inactive).
          </Typography>
          
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUpload />}
            sx={{ mr: 2 }}
          >
            Choose File
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </Button>
          
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {file.name}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
          </Alert>
        )}

        {results && (
          <Alert severity={results.failed === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Import Results:</Typography>
            <Typography variant="body2">
              • Total records: {results.total}
            </Typography>
            <Typography variant="body2">
              • Successfully imported: {results.successful}
            </Typography>
            {results.failed > 0 && (
              <Typography variant="body2">
                • Failed: {results.failed}
              </Typography>
            )}
            
            {results.validationErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error">
                  Validation errors:
                </Typography>
                <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                  {results.validationErrors.join("\n")}
                </pre>
              </Box>
            )}
            
            {results.importErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error">
                  Import errors:
                </Typography>
                <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                  {results.importErrors.join("\n")}
                </pre>
              </Box>
            )}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Importing vouchers...</Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {results ? "Close" : "Cancel"}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!file || loading}
        >
          {loading ? "Importing..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VoucherImport;